import type { RuleGraphDto } from "@intelligems/ig-types";
import { IgLoading, IgTypography } from "@intelligems/ig-theme";
import { Box, keyframes, Stack } from "@mui/material";

import type { ReactNode } from "react";

import { GraphBuildingState } from "./GraphBuildingState";
import { RULE_GRAPH_VIEWPORT_CONTROL_INSET } from "./ruleGraphControlDimensions/RULE_GRAPH_VIEWPORT_CONTROL_INSET";
import { RuleGraphView } from "./RuleGraphView";
import type {
  RuleGraphOverlaysByEdgeId,
  RuleGraphOverlaysByNodeId,
} from "./types";

/** Replayed (via the wrapper's key) when the skeleton resolves into the
 * real graph, so the finished structure fades in rather than popping. */
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";

const VISUALLY_HIDDEN = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: 1,
  margin: -1,
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: 1,
} as const;

/**
 * The Selection logic tab body: the rule-graph canvas over the rule's stored
 * graph artifact, or an honest empty state when none exists yet.
 *
 * The canvas is a reader, not a control panel. Running a cart against the rule
 * belongs to the Atlas conversation beside it — the overlays here are the
 * result of a run the chat asked for, never of a click on this tab.
 */
export const SelectionLogicTab = ({
  graph,
  isLoading = false,
  isPending = false,
  isRebuilding = false,
  overlaysByNodeId,
  overlaysByEdgeId,
  canvasAction,
  emptyHint,
  pendingHint,
}: {
  graph: RuleGraphDto | null;
  isLoading?: boolean;
  /**
   * The graph is being BUILT, not merely fetched: a save with no graph
   * regenerates one asynchronously. Says so instead of reporting the rule as
   * having no selection logic, which is what a reader would otherwise take
   * from an empty canvas seconds after creating a rule.
   */
  isPending?: boolean;
  /**
   * The existing graph is being REGENERATED (the admin regenerate lane): the
   * canvas keeps its real structure but every card shimmers, nothing is
   * clickable, and scenario overlays are withheld until the rebuild lands.
   */
  isRebuilding?: boolean;
  overlaysByNodeId?: RuleGraphOverlaysByNodeId;
  overlaysByEdgeId?: RuleGraphOverlaysByEdgeId;
  /** Bottom-left action over the graph canvas (admin tools). */
  canvasAction?: ReactNode;
  /** Replaces the default explanation of how a graph comes to exist. */
  emptyHint?: string;
  /** Replaces the default explanation of when the pending graph appears. */
  pendingHint?: string;
}) => {
  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
        <IgLoading />
      </Stack>
    );
  }

  if (graph === null && isPending) {
    return (
      <GraphBuildingState
        title="Building the selection-logic graph"
        hint={
          pendingHint ??
          "It appears here as soon as this rule finishes going live."
        }
      />
    );
  }

  if (graph === null) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ flex: 1, px: 4, textAlign: "center" }}
      >
        <IgTypography variant="body1" fontWeight={600}>
          No selection-logic graph yet
        </IgTypography>
        <IgTypography variant="body2" color="text.secondary">
          {/* Saving is what deploys, so "saving generates one" is true for
              every org — and the version-and-publish phrasing this replaced was
              already pointing a basic org at a tab it cannot open. A caller
              that wants the expert wording passes `emptyHint`. */}
          {emptyHint ?? "Saving this rule will generate one."}
        </IgTypography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ flex: 1, minHeight: 0, p: 2 }} spacing={1}>
      {isRebuilding ? (
        <IgTypography aria-live="polite" role="status" sx={VISUALLY_HIDDEN}>
          Rebuilding the selection-logic graph
        </IgTypography>
      ) : null}
      <Box
        data-testid="rule-graph-canvas-frame"
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          position: "relative",
        }}
      >
        {canvasAction ? (
          <Box
            sx={{
              bottom: RULE_GRAPH_VIEWPORT_CONTROL_INSET,
              left: RULE_GRAPH_VIEWPORT_CONTROL_INSET,
              position: "absolute",
              zIndex: 5,
            }}
          >
            {canvasAction}
          </Box>
        ) : null}
        <Box
          // Remounting on the skeleton↔graph flip replays the fade, so the
          // rebuilt structure eases in instead of popping. The canvas action
          // stays outside this keyed subtree to preserve its DOM position.
          key={isRebuilding ? "skeleton" : "graph"}
          sx={{
            animation: `${fadeIn} 240ms ease-out`,
            display: "flex",
            flex: 1,
            minHeight: 0,
            [REDUCED_MOTION]: { animation: "none" },
          }}
        >
          {isRebuilding ? (
            <RuleGraphView graph={graph} skeleton />
          ) : (
            <RuleGraphView
              graph={graph}
              overlaysByNodeId={overlaysByNodeId}
              overlaysByEdgeId={overlaysByEdgeId}
            />
          )}
        </Box>
      </Box>
    </Stack>
  );
};
